// src/CommonMethods/authSession.js

const session = sessionStorage;
const local = localStorage;

// Session cookie: marker for "same browser session" (no Max-Age/Expires = dropped when browser closes)
const SESSION_COOKIE_NAME = 'arcm_browser_session';

const getSessionCookie = () => {
    if (typeof document === 'undefined' || !document.cookie) return false;
    return document.cookie.split(';').some((part) =>
        part.trim().startsWith(SESSION_COOKIE_NAME + '=')
    );
};

const setSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=1; path=/; SameSite=Lax`;
    } catch (error) {
        console.error('Error setting session cookie:', error);
    }
};

const clearSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=; path=/; Max-Age=0`;
    } catch (error) {
        console.error('Error clearing session cookie:', error);
    }
};

/**
 * Call once before the app renders. If the session cookie is missing
 * (e.g. browser was closed and reopened), clear auth storage so the app
 * does not use stale tokens and avoids blank screen / wrong redirect.
 */
export const clearStorageIfNewBrowserSession = () => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (!getSessionCookie()) {
        clearAuthSession();
    }
};

// Event keys for cross-tab communication
const LOGOUT_EVENT_KEY = 'arcm_logout_event';
const FORCED_LOGOUT_EVENT_KEY = 'arcm_forced_logout_event';
const LOGIN_EVENT_KEY = 'arcm_login_event';
const SESSION_CONFIRMED_KEY = 'arcm_session_confirmed';

// Auth keys that should be synced between storages
const AUTH_STORAGE_KEYS = new Set([
    'IdToken',
    'RefreshToken',
    'AccessToken',
    'email',
    'user_id',
    'id',
    'user_type',
    'session_token',
    'custom:is_first_time_login',
    'user-info',
    'browser_trust_duration',
    'custom-status',
    'verify_otp_session_token',
    'preference_id',
    'token',
    'currentTimestamp',
    'session_ip_address',
    'session_ip_timestamp',
    'first_name',
    'last_name',
]);

// Non-auth keys preserved during logout
const PRESERVE_KEYS = ['arcm_trust_browser_token', 'LogoutfromAllTabs'];

// Helper functions
const setItem = (storage, key, value) => {
    try {
        storage.setItem(key, value);
    } catch (error) {
        // Silent fail - storage quota exceeded or disabled
    }
};

const readItem = (storage, key) => {
    try {
        return storage.getItem(key);
    } catch (error) {
        return null;
    }
};

const removeItem = (storage, key) => {
    try {
        storage.removeItem(key);
    } catch (error) {
        // Silent fail
    }
};

// Set auth item in both storages
export const setAuthItem = (key, value) => {
    if (AUTH_STORAGE_KEYS.has(key)) {
        setItem(session, key, value);
        setItem(local, key, value);
    } else {
        setItem(local, key, value);
    }
};

// Get auth item (sessionStorage first, then localStorage)
export const getAuthItem = (key) => {
    return readItem(session, key) || readItem(local, key);
};

// Remove auth item from both storages
export const removeAuthItem = (key) => {
    if (AUTH_STORAGE_KEYS.has(key)) {
        removeItem(session, key);
        removeItem(local, key);
    } else {
        removeItem(local, key);
    }
};

// Sync session from localStorage to sessionStorage
export const syncSessionFromLocalStorage = () => {
    AUTH_STORAGE_KEYS.forEach((key) => {
        const hasSessionValue = readItem(session, key);
        if (!hasSessionValue) {
            const localValue = readItem(local, key);
            if (localValue) {
                setItem(session, key, localValue);
            }
        }
    });
};

// Persist auth session (stores all tokens)
export const persistAuthSession = (authData) => {
    Object.keys(authData).forEach((key) => {
        if (authData[key] !== null && authData[key] !== undefined) {
            setAuthItem(key, authData[key].toString());
        }
    });
    // Promote staged session to fully persisted — clear pending flag if present
    removeItem(session, 'duplicate_session_pending');
    setSessionCookie();
};

// Stage auth session to sessionStorage only (not localStorage).
// Used during duplicate session pending state so same-tab API calls work,
// but new tabs cannot read the tokens from localStorage and bypass the modal.
// Sets duplicate_session_pending so the router blocks authenticated routes.
export const stageAuthSession = (authData) => {
    Object.keys(authData).forEach((key) => {
        if (authData[key] !== null && authData[key] !== undefined) {
            setItem(session, key, authData[key].toString());
        }
    });
    setItem(session, 'duplicate_session_pending', 'true');
    setSessionCookie();
};

// Clear auth session (preserves non-auth keys)
export const clearAuthSession = () => {
    const preservedData = {};
    PRESERVE_KEYS.forEach((key) => {
        const value = readItem(local, key);
        if (value !== null) {
            preservedData[key] = value;
        }
    });

    AUTH_STORAGE_KEYS.forEach((key) => {
        removeItem(session, key);
        removeItem(local, key);
    });

    // Clear duplicate session pending flag if set
    removeItem(session, 'duplicate_session_pending');

    // Clear cross-tab event signal keys (transient timestamps, not auth data)
    const EVENT_KEYS = [
        LOGOUT_EVENT_KEY,
        FORCED_LOGOUT_EVENT_KEY,
        LOGIN_EVENT_KEY,
        SESSION_CONFIRMED_KEY,
        'arcm_duplicate_session_resolved', // legacy key from prior implementation
    ];
    EVENT_KEYS.forEach((key) => removeItem(local, key));

    Object.keys(preservedData).forEach((key) => {
        setItem(local, key, preservedData[key]);
    });

    clearSessionCookie();
};

// Check if valid session exists
export const hasValidSession = () => {
    return !!getAuthItem('IdToken');
};

// Broadcast logout event to other tabs
export const broadcastLogout = () => {
    const timestamp = Date.now().toString();
    setItem(local, LOGOUT_EVENT_KEY, timestamp);
};

// Broadcast WS-forced logout (duplicate session from another device).
// Sibling old-session tabs receive this and show the "another login session" toast
// instead of the "logged out in another tab" toast.
export const broadcastForcedLogout = () => {
    setItem(local, FORCED_LOGOUT_EVENT_KEY, Date.now().toString());
};

// Broadcast login event to other tabs
export const broadcastLogin = () => {
    setItem(local, LOGIN_EVENT_KEY, Date.now().toString());
};

// Broadcast that a duplicate session modal was confirmed (OTP path).
// Pending tabs listen for this to dismiss their stale modal immediately,
// without waiting for the full OTP + preferences flow to complete.
export const broadcastSessionConfirmed = () => {
    setItem(local, SESSION_CONFIRMED_KEY, Date.now().toString());
};

// Subscribe to auth events (login/logout) from other tabs
export const subscribeToAuthEvents = (handler) => {
    const listener = (event) => {
        if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
            // Pass the broadcast timestamp to the handler so it can determine if this tab initiated the logout
            const broadcastTimestamp = parseInt(event.newValue, 10);
            handler('logout', broadcastTimestamp);
        }
        if (event.key === FORCED_LOGOUT_EVENT_KEY && event.newValue) {
            handler('forced_logout');
        }
        if (event.key === LOGIN_EVENT_KEY && event.newValue) {
            handler('login');
        }
        if (event.key === SESSION_CONFIRMED_KEY && event.newValue) {
            handler('session_confirmed');
        }
    };
    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
};

